import * as React from "react";
import {formatDateDMY, getAmountWithDecimalDigits} from "../../CommonFunctions";
import {Link} from "react-router-dom";
import {
    Policy,
    POLICY_STATUS_PENDING_RENEWAL,
    PREMIUM_TYPE_MONTHLY,
    Renewal,
    RENEWAL_PROPOSAL_EDIT_VIEW_JOURNEY_URL,
    SESSION_STORAGE_KEY_RENEWAL_PROPOSAL_CLOSED
} from "@ferry/journey-services";
import {Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import {getPrettifiedInteractionBrandName, resourcesHelper} from "@ferry/portal-core";

interface IAutoRenewalProps {
    currentPolicy: Policy;
    renewalProposal: Renewal;
    showHideWillBeRenewedConfirmation: (display: boolean) => void;
    setPerformRenewal: (a: boolean) => void;
}

export const AutoRenewalInviteAfterLogin: React.FC<IAutoRenewalProps> = ({
    currentPolicy,
    renewalProposal,
    showHideWillBeRenewedConfirmation,
    setPerformRenewal
}) => {
    const [isOpen, setIsOpen] = React.useState(true);
    const {make, model, registrationNumber} = currentPolicy.insuredVehicles[0];
    const {firstName} = currentPolicy.policyHolder;
    const renewalStartDate = formatDateDMY(renewalProposal.headerInfo.startDate);
    const policyEndDate = formatDateDMY(currentPolicy.endDate);
    const premiumType: string = renewalProposal.factors.premiumType;
    const isPromotionApplied = renewalProposal.promotions?.promotionsApplied;
    const isMonthly = premiumType === PREMIUM_TYPE_MONTHLY;
    const brandName: string = getPrettifiedInteractionBrandName(currentPolicy.interaction.brandName);

    const renewalAnnualPremium: number = renewalProposal.factors.annualPremium;
    const renewalMonthlyPremium: number = renewalProposal.paymentDetails.instalmentPlan.monthlyPayment;
    const renewalAmountToPay: number = renewalProposal.factors.amountToPay;
    const renewalPromotionsTotalAmount: number = renewalProposal.promotions?.promotionsTotalValue;

    const annualLastYearPrice: number = currentPolicy.interaction.factors.annualPremium;
    const monthlyLastYearPrice: number = currentPolicy.interaction.factors.monthlyPremium;
    const renewalDocUrl = RENEWAL_PROPOSAL_EDIT_VIEW_JOURNEY_URL + renewalProposal._id;
    const isRenewalStarted = renewalProposal.status === POLICY_STATUS_PENDING_RENEWAL;

    const hideModal = (): void => {
        window.sessionStorage.setItem(SESSION_STORAGE_KEY_RENEWAL_PROPOSAL_CLOSED + currentPolicy.policyNumber, "true");
        setIsOpen(false);
    }

    const setRenewal = (): void => {
        setPerformRenewal(true);
        showHideWillBeRenewedConfirmation(true);
        hideModal();
    }

    return (
        <Modal className="my-2 manual-renewal-modal" isOpen={isOpen} toggle={hideModal}>
            <ModalHeader close={<button className="btn-close" onClick={hideModal}/>}/>
            <ModalBody className="pt-0">
                <div>
                    <div className="mr-4">
                        <div className="d-flex success-icon-container">
                            <img className="d-block mb-3 mr-3 success-icon"
                                 src={`/${resourcesHelper.THEME_PATH}/img/check-mark-green-light.svg`}
                                 alt="Process complete"
                                 aria-label="Process complete picture" width={32} height={32}/>
                            <h4 className="m-0 pb-20 font-weight-bold">Hi {firstName},</h4>
                        </div>
                        <p className="m-0 pt-20">
                            Your current car cover for your {make} {model} ({registrationNumber}) is
                            due {isRenewalStarted ? <span>for renewal </span> : <span>to expire </span>}
                            on {isRenewalStarted ? renewalStartDate : policyEndDate}.
                        </p>
                        <div className="renewal-price-comparison">
                            <div className="d-flex w-100 justify-content-between border-0 pb-0">
                                <div className="d-flex flex-column w-auto justify-content-center">
                                    <span className="font-weight-bold m-0">Total cost</span>
                                    {isPromotionApplied && <span className="font-weight-bold m-0">Voucher</span>}
                                </div>
                                <div className="d-flex flex-column w-auto justify-content-end align-items-end">
                                    <span className="font-weight-bold m-0">
                                        {getAmountWithDecimalDigits(renewalAnnualPremium)}
                                    </span>
                                    {isPromotionApplied &&
                                        <span className="font-weight-bold m-0">
                                            - {getAmountWithDecimalDigits(renewalPromotionsTotalAmount)}*
                                        </span>}
                                </div>
                            </div>
                            <div className="d-flex w-100 justify-content-between">
                                <div className="d-flex flex-column w-auto justify-content-center">
                                    <span className="font-weight-bold m-0">Renewal price</span>
                                    {isMonthly && <span className="pt-6">
                                        You will pay {getAmountWithDecimalDigits(renewalMonthlyPremium)} / month</span>}
                                </div>
                                <div className="d-flex flex-column w-auto justify-content-end align-items-end">
                                    <h5 className="font-weight-bold m-0">{getAmountWithDecimalDigits(renewalAmountToPay)}</h5>
                                </div>
                            </div>
                            <div className="d-flex w-100 justify-content-between">
                                <div className="d-flex flex-column w-auto">
                                    <span className="font-weight-bold">Last year's price</span>
                                    {isMonthly && <span className="pt-6">
                                        You paid {getAmountWithDecimalDigits(monthlyLastYearPrice)} / month</span>}
                                </div>
                                <div className="d-flex w-auto justify-content-end align-items-center">
                                    <span className="font-weight-bold m-0">
                                        {getAmountWithDecimalDigits(annualLastYearPrice)}
                                        <sup>{isPromotionApplied ? '**' : '*'}</sup>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="last-year-price-explanation">
                            {isPromotionApplied &&
                                <span><sup>*</sup> The price includes a {brandName} insurance voucher</span>
                            }
                            <span><sup>{isPromotionApplied ? '**' : '*'}</sup> Including any changes made, excludes any promotions</span>
                            <span>All figures include Insurance Premium Tax.</span>
                        </div>
                        <p className="font-weight-bold">Please take a minute to have a good read of your&nbsp;
                            <Link className="color-cyan-blue"
                                  to={renewalDocUrl}>
                                <span className="hyperlink">renewal documents</span>
                            </Link>
                            , just to make sure all your details are correct
                        </p>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <div className="d-flex flex-column w-100">
                    <button type="button"
                            className="btn btn-primary btn-blue-dashboard"
                            onClick={setRenewal}>Renew your policy now
                    </button>
                    <button type="button" className="btn btn-white-dashboard"
                            onClick={hideModal}>Not just yet
                    </button>
                </div>
            </ModalFooter>
        </Modal>
    )
}
